import { useEffect, useState, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { X, CreditCard, Loader2, Zap, Check } from 'lucide-react';
import api from '../../services/api';

let stripePromise = null;
function getStripePromise() {
    if (!stripePromise) {
        stripePromise = api.get('/stripe/config.php')
            .then(({ data }) => loadStripe(data.data.public_key));
    }
    return stripePromise;
}

const formatPrice = (p) => `${parseFloat(p).toFixed(2).replace('.', ',')} €`;

/**
 * PackBoosterCheckoutModal
 *
 * Props :
 *   thematiqueId : number|null — scopes doc credits to that thematique.
 *   offers       : array from useCreditPacks (backend pack_offers catalog).
 *                  If empty or missing, we fall back to the single `offer` / `content` props.
 *   offer        : single offer pre-selected (used when invoked from a CTA that already knows).
 *   content      : { doc, mono } fallback (legacy shape).
 *   priceEur     : number fallback.
 *   onClose      : () => void
 *   onSuccess    : () => void
 */
export default function PackBoosterCheckoutModal({
    thematiqueId = null,
    offers = null,
    offer: preSelectedOffer = null,
    priceEur = 10,
    content = { doc: 30, mono: 200 },
    onClose,
    onSuccess,
}) {
    const catalog = useMemo(() => Array.isArray(offers) ? offers : (preSelectedOffer ? [preSelectedOffer] : []), [offers, preSelectedOffer]);
    const [selectedOfferId, setSelectedOfferId] = useState(
        preSelectedOffer?.id || catalog[0]?.id || null
    );
    const selectedOffer = useMemo(
        () => catalog.find(o => o.id === selectedOfferId) || catalog[0] || null,
        [catalog, selectedOfferId]
    );

    const [clientSecret, setClientSecret] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [simulating, setSimulating] = useState(false);

    const isDev = import.meta.env.DEV || new URLSearchParams(window.location.search).has('simulate');

    useEffect(() => {
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKey);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleKey);
            document.body.style.overflow = '';
        };
    }, [onClose]);

    const handleBuy = async () => {
        setError(null);
        setLoading(true);
        try {
            const { data } = await api.post('/custom/pack-checkout.php', {
                user_thematique_id: thematiqueId || undefined,
                offer_id: selectedOffer?.id || undefined,
            });
            if (data.success && data.data?.client_secret) {
                setClientSecret(data.data.client_secret);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur');
        }
        setLoading(false);
    };

    const handleSimulate = async () => {
        setSimulating(true);
        setError(null);
        try {
            const { data } = await api.post('/custom/pack-checkout.php', {
                user_thematique_id: thematiqueId || undefined,
                offer_id: selectedOffer?.id || undefined,
                simulate: true,
            });
            if (data.data?.simulated) {
                onSuccess?.();
                setTimeout(() => onClose(), 1500);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur');
            setSimulating(false);
        }
    };

    // Display fallbacks when the catalog isn't loaded
    const displayPrice = selectedOffer?.price_eur ?? priceEur;
    const displayDoc   = selectedOffer?.doc_credits ?? content.doc;
    const displayMono  = selectedOffer?.mono_credits ?? content.mono;
    const displayLabel = selectedOffer?.label ?? 'Pack IA';

    return (
        <div className="checkout-modal-overlay" onClick={onClose}>
            <div className="checkout-modal checkout-modal--wide" onClick={e => e.stopPropagation()}>
                <div className="checkout-modal__header">
                    <span className="checkout-modal__title">
                        {clientSecret
                            ? `Paiement — ${displayLabel} (${formatPrice(displayPrice)})`
                            : <><Zap size={18} /> {displayLabel}</>
                        }
                    </span>
                    <button className="checkout-modal__close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>
                <div className="checkout-modal__body">
                    {error && <div className="alert alert-error" style={{ margin: 16 }}>{error}</div>}

                    {simulating && (
                        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                            <div className="spinner" style={{ margin: '0 auto 12px' }} />
                            <p><strong>Paiement simulé</strong> — Crédits ajoutés !</p>
                        </div>
                    )}

                    {!clientSecret && !simulating && (
                        <div className="sub-modal-plans">
                            {catalog.length > 1 && (
                                <div className="share-panel__pack-grid">
                                    {catalog.map((o, idx) => {
                                        const popular = idx === Math.floor(catalog.length / 2);
                                        const active = selectedOfferId === o.id;
                                        return (
                                            <div key={o.id}
                                                className={`share-panel__pack-card ${popular ? 'share-panel__pack-card--popular' : ''} ${active ? 'share-panel__pack-card--active' : ''}`}
                                                onClick={() => setSelectedOfferId(o.id)}
                                                style={{ cursor: 'pointer' }}>
                                                {popular && <span className="share-panel__pack-badge">Populaire</span>}
                                                <Zap size={20} />
                                                <div className="share-panel__pack-nb">{o.label}</div>
                                                <div className="share-panel__pack-price">{formatPrice(o.price_eur)}</div>
                                                <div className="share-panel__pack-unit">+{o.doc_credits} places/générations IA · +{o.mono_credits} questions à l’unité</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <div className="pack-booster-card">
                                <div className="pack-booster-card__icon"><Zap size={28} /></div>
                                <div className="pack-booster-card__price">{parseFloat(displayPrice).toFixed(0)}<span>€</span></div>
                                <div className="pack-booster-card__label">{selectedOffer?.description || 'Achat unique — crédits à vie'}</div>
                                <ul className="pack-booster-card__list">
                                    <li>
                                        <Check size={14} />
                                        <span><strong>+{displayDoc} places dans cette thématique</strong> et <strong>+{displayDoc} générations IA</strong> (PDF ou prompt).</span>
                                    </li>
                                    <li>
                                        <Check size={14} />
                                        <span><strong>+{displayMono} questions IA à l’unité</strong> — pour enrichir vos sujets existants.</span>
                                    </li>
                                </ul>
                            </div>

                            <button className="btn btn-primary sub-modal-cta"
                                onClick={handleBuy}
                                disabled={loading || !selectedOffer}>
                                {loading
                                    ? <><Loader2 size={16} className="spin" /> Chargement…</>
                                    : <><CreditCard size={16} /> Acheter — {formatPrice(displayPrice)}</>
                                }
                            </button>
                            {isDev && (
                                <button className="btn btn-sm"
                                    style={{ display: 'block', margin: '8px auto 0', fontSize: '0.75em', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}
                                    onClick={handleSimulate}>
                                    Simuler (dev)
                                </button>
                            )}
                            <p className="sub-modal-note">Paiement unique. Les crédits n'expirent pas — ils se consomment au fil de l'eau, au-delà de vos quotas initiaux.</p>
                        </div>
                    )}

                    {clientSecret && !simulating && (
                        <div>
                            <button className="sub-modal-back"
                                onClick={() => { setClientSecret(null); setError(null); }}>
                                ← Annuler
                            </button>
                            <EmbeddedCheckoutProvider stripe={getStripePromise()} options={{ clientSecret }}>
                                <EmbeddedCheckout />
                            </EmbeddedCheckoutProvider>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
